import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { findVerse } from '@rb/shared/admin-verses';
import { fetchCdnCollection } from '@/client/cdn-sheets';
import { grantPass, type GrantPassResult } from '@/client/admin.client';
import type { ClientProduct } from '@/shop/vxshop-mapping';
import { useActiveVerseStore } from '@/verse/active-verse.store';
import {
  presetToOpts,
  readPassMeta,
  type PassGrantPreset,
} from '@/components/admin/pass-grant-presets';

interface Props {
  account: string;
  state: Record<string, unknown> | null;
  /** 지급 성공 후 부모가 getUserDetail 을 재조회하도록 트리거. */
  onApplied: () => void;
}

interface PassRow {
  productId: string;
  activatedAt: number;
  expiresAt: number;
  claimedLevel: number;
  cycles?: number;
}

const PRESETS: Array<{ id: PassGrantPreset; label: string; hint: string }> = [
  { id: 'fresh', label: '방금 구매', hint: '활성 · L1 해금 · 미수령' },
  { id: 'aged', label: 'N일 전 구매', hint: '활성 · L(N+1) 까지 해금' },
  { id: 'expired_unclaimed', label: '만료 + 미수령 잔여', hint: '기존 서브탭 유지' },
  { id: 'expired_claimed', label: '만료 + 전량 수령', hint: '기존 서브탭 소멸' },
];

function readPasses(state: Record<string, unknown> | null): PassRow[] {
  const passes = state?.passes as Record<string, Omit<PassRow, 'productId'>> | undefined;
  if (!passes) return [];
  return Object.entries(passes).map(([productId, p]) => ({ productId, ...p }));
}

function fmt(ms: number): string {
  return ms > 0 ? new Date(ms).toISOString().slice(0, 16).replace('T', ' ') : '-';
}

/** 번들 상품(passGroup 보유) 여부. 번들은 top-level passTrack 이 없어
 * expired_claimed 프리셋(만료+전량 수령)이 조용히 no-op 된다. */
function isBundleProduct(metadataJson: string): boolean {
  try {
    return Array.isArray((JSON.parse(metadataJson) as { passGroup?: unknown }).passGroup);
  } catch {
    return false;
  }
}

export default function PassGrantPanel({ account, state, onApplied }: Props) {
  const activeVerseId = useActiveVerseStore((s) => s.activeVerseId);
  const env = activeVerseId ? (findVerse(activeVerseId)?.env ?? 'staging') : 'staging';

  const [products, setProducts] = useState<ClientProduct[]>([]);
  const [productId, setProductId] = useState('');
  const [preset, setPreset] = useState<PassGrantPreset>('fresh');
  const [agedDays, setAgedDays] = useState(5);
  const [stack, setStack] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<GrantPassResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const all = await fetchCdnCollection<ClientProduct[]>(env, 'clientProducts');
        const passes = all.filter((p) => {
          try {
            return (JSON.parse(p.metadata) as { category?: string }).category === 'PASS';
          } catch {
            return false;
          }
        });
        if (cancelled) return;
        setProducts(passes);
        // env 전환으로 이전 목록의 productId 가 새 목록에 없으면 첫 항목으로
        // 대체한다(유지 시 "지급" 버튼만 조용히 비활성화되고 이유가 안 보임).
        setProductId((cur) =>
          passes.some((p) => p.productId === cur) ? cur : (passes[0]?.productId ?? '')
        );
        setError('');
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [env]);

  const selected = useMemo(
    () => products.find((p) => p.productId === productId) ?? null,
    [products, productId]
  );

  const isBundle = useMemo(
    () => (selected ? isBundleProduct(selected.metadata) : false),
    [selected]
  );

  // 번들 상품으로 바뀌었는데 expired_claimed 가 선택돼 있으면(번들엔 no-op) 유효한
  // 프리셋으로 되돌린다.
  useEffect(() => {
    if (isBundle && preset === 'expired_claimed') {
      setPreset('fresh');
    }
  }, [isBundle, preset]);

  const handleGrant = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const meta = readPassMeta(selected.metadata);
      const { elapsedDays, claimedLevel } = presetToOpts(preset, meta, agedDays);
      const res = await grantPass(account, selected.productId, selected.metadata, {
        elapsedDays,
        // stack(재구매 스택) 은 기존 수령 진행도를 보존해야 실구매 재구매와 같다.
        // claimedLevel 을 보내지 않으면 서버가 기존 값을 유지한다.
        claimedLevel: stack ? undefined : claimedLevel,
        mode: stack ? 'stack' : 'reset',
      });
      setResult(res);
      onApplied();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [account, selected, preset, agedDays, stack, onApplied]);

  const rows = readPasses(state);

  return (
    <div className="space-y-3">
      {/* 위 UserProgressDevPanel 과의 경계선 — 부모(UserManagement)는 두 패널 사이에 구분선을 두지 않는다. */}
      <Separator />
      <div className="text-sm font-medium">
        패스 지급 · CDN {env}/clientProducts 기준 ({products.length}종)
      </div>

      {rows.length > 0 && (
        <div className="text-xs font-mono space-y-1">
          {rows.map((r) => (
            <div key={r.productId}>
              {r.productId} · 만료 {fmt(r.expiresAt)} · claimed L{r.claimedLevel} · cycles{' '}
              {r.cycles ?? 1}
            </div>
          ))}
        </div>
      )}

      <select
        className="border rounded px-2 py-1 text-sm w-full"
        value={productId}
        onChange={(e) => setProductId(e.target.value)}
      >
        {products.map((p) => {
          const gen = (JSON.parse(p.metadata) as { entitlementGen?: number }).entitlementGen ?? 0;
          return (
            <option key={p.productId} value={p.productId}>
              {p.productId} · {gen >= 2 ? 'v2 신규' : 'v1 구'} ·{' '}
              {p.isActive ? '판매중' : '판매종료'}
            </option>
          );
        })}
      </select>

      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => {
          const disabled = p.id === 'expired_claimed' && isBundle;
          return (
            <Button
              key={p.id}
              size="sm"
              variant={preset === p.id ? 'default' : 'outline'}
              onClick={() => setPreset(p.id)}
              disabled={disabled}
              title={
                disabled ? '번들은 멤버 패스를 개별 지급해야 수령 상태를 만들 수 있습니다' : p.hint
              }
            >
              {p.label}
            </Button>
          );
        })}
      </div>
      {isBundle && (
        <div className="text-xs text-muted-foreground">
          번들 상품은 top-level 수령 트랙이 없어 "만료 + 전량 수령" 프리셋이 동작하지 않습니다 —
          멤버 패스를 개별 지급하세요.
        </div>
      )}

      {preset === 'aged' && (
        <div className="flex items-center gap-2">
          <Label className="text-xs">경과 일수</Label>
          <Input
            type="number"
            className="w-24"
            value={agedDays}
            onChange={(e) => setAgedDays(Number(e.target.value))}
          />
        </div>
      )}

      <div className="flex items-center gap-2">
        <Checkbox
          id="pass-grant-stack"
          checked={stack}
          onCheckedChange={(v) => setStack(v === true)}
        />
        <Label htmlFor="pass-grant-stack" className="text-xs">
          재구매 스택으로 지급 (기본은 기존 오너십 초기화 후 첫 구매)
        </Label>
      </div>

      <Button size="sm" disabled={busy || !selected} onClick={handleGrant}>
        {busy ? '지급 중…' : '지급'}
      </Button>

      {result && (
        <div className="text-xs space-y-1">
          <div>
            지급: {result.granted.join(', ')} / 퍽: {result.entitlements.join(', ') || '없음'}
          </div>
          <div>
            만료 {fmt(result.expiresAt)} · claimed L{result.claimedLevel} / 해금 L
            {result.unlockedLevel} / 전체 L{result.totalLevels}
          </div>
          {(result.shiftedPasses > 0 || result.shiftedEntitlements > 0) && (
            <div>
              시간 되감기 부수효과: {result.shiftedPasses}개 패스 / {result.shiftedEntitlements}개
              퍽 되감김 (elapsedDays 는 대상 계정의 모든 패스·퍽에 적용됨)
            </div>
          )}
          {result.clamped && (
            <Badge variant="secondary">claimedLevel 이 해금 상한으로 클램프됨</Badge>
          )}
        </div>
      )}

      {error && <div className="text-xs text-red-500">{error}</div>}
    </div>
  );
}
